import React, { useState, useEffect } from 'react';
import { accountingApi } from '../../../services/accountingApi';

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = [`${CURRENT_YEAR}-${CURRENT_YEAR + 1}`, `${CURRENT_YEAR - 1}-${CURRENT_YEAR}`, `${CURRENT_YEAR + 1}-${CURRENT_YEAR + 2}`];

const getProgressColor = (pct) => {
  if (pct <= 75) return 'green';
  if (pct <= 95) return 'yellow';
  return 'red';
};

const Budget = () => {
  const [budget, setBudget]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editRow, setEditRow]   = useState(null);
  const [form, setForm]         = useState({ category: '', type: 'Income', budgeted: '', actual: '' });
  const [financialYear, setFinancialYear] = useState(YEAR_OPTIONS[0]);

  const loadBudget = (year) => {
    setLoading(true);
    accountingApi.getBudget(year)
      .then(res => {
        const toArr = r => Array.isArray(r) ? r : Array.isArray(r.data) ? r.data : Array.isArray(r.data?.data) ? r.data.data : [];
        setBudget(toArr(res));
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadBudget(financialYear); }, [financialYear]);

  const totalBudgetedIncome  = budget.filter(b => (b.type||'').toLowerCase() === 'income').reduce((s, b) => s + (parseFloat(b.budgeted_amount || b.budgeted) || 0), 0);
  const totalActualIncome    = budget.filter(b => (b.type||'').toLowerCase() === 'income').reduce((s, b) => s + (parseFloat(b.actual_amount  || b.actual)   || 0), 0);
  const totalBudgetedExpense = budget.filter(b => (b.type||'').toLowerCase() === 'expense').reduce((s, b) => s + (parseFloat(b.budgeted_amount || b.budgeted) || 0), 0);
  const totalActualExpense   = budget.filter(b => (b.type||'').toLowerCase() === 'expense').reduce((s, b) => s + (parseFloat(b.actual_amount  || b.actual)   || 0), 0);
  const netBudget = totalBudgetedIncome - totalBudgetedExpense;
  const netActual = totalActualIncome   - totalActualExpense;

  const openAdd = () => {
    setEditRow(null);
    setForm({ category: '', type: 'Income', budgeted: '', actual: '' });
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditRow(row.id);
    setForm({
      category: row.account?.name || row.category || '',
      type:     row.type || 'Income',
      budgeted: row.budgeted_amount || row.budgeted || '',
      actual:   row.actual_amount   || row.actual   || '',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.category) return;
    setSaving(true);
    try {
      const lines = editRow
        ? budget.map(b => b.id === editRow
            ? { ...b, budgeted_amount: parseFloat(form.budgeted) || 0, actual_amount: parseFloat(form.actual) || 0 }
            : b)
        : [...budget, { category: form.category, type: form.type, budgeted_amount: parseFloat(form.budgeted) || 0, actual_amount: parseFloat(form.actual) || 0 }];

      await accountingApi.saveBudget({ financial_year: financialYear, lines });
      setShowModal(false);
      loadBudget(financialYear);
    } catch (e) {
      alert(`Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const lines = budget.filter(b => b.id !== id);
    try {
      await accountingApi.saveBudget({ financial_year: financialYear, lines });
      loadBudget(financialYear);
    } catch (e) {
      alert(`Delete failed: ${e.message}`);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      
      <div className="acc-stats-row">
        {[
          { label: 'Budgeted Income', value: `$${totalBudgetedIncome.toLocaleString()}`, color: 'green' },
          { label: 'Actual Income', value: `$${totalActualIncome.toLocaleString()}`, color: 'blue' },
          { label: 'Budgeted Expense', value: `$${totalBudgetedExpense.toLocaleString()}`, color: 'orange' },
          { label: 'Actual Expense', value: `$${totalActualExpense.toLocaleString()}`, color: 'red' },
          { label: 'Net Budget', value: `$${netBudget.toLocaleString()}`, color: 'purple' },
          { label: 'Net Actual', value: `$${netActual.toLocaleString()}`, color: netActual >= 0 ? 'green' : 'red' },
        ].map(s => (
          <div key={s.label} className={`acc-stat-card ${s.color}`}>
            <div className="acc-stat-label">{s.label}</div>
            <div className="acc-stat-value">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="table-content-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '1rem' }}>
          <h3 className="acc-card-title" style={{ margin: 0 }}>Budget vs Actual — {financialYear}</h3>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <div className="tabs-search-wrapper" style={{ display: 'flex', alignItems: 'center', background: 'var(--acc-surface)', padding: '0.2rem 0.5rem', borderRadius: 'var(--acc-radius)', border: '1px solid var(--acc-border)', boxShadow: 'var(--acc-shadow-sm)' }}>
              <select
                value={financialYear}
                onChange={e => setFinancialYear(e.target.value)}
                style={{ minWidth: '150px', height: '34px', padding: '0 0.5rem', fontSize: '0.875rem', border: 'none', background: 'transparent', outline: 'none', color: 'var(--acc-text-main)', cursor: 'pointer', fontWeight: 600 }}
              >
                {YEAR_OPTIONS.map(y => <option key={y}>{y}</option>)}
              </select>
            </div>
            
            <button className="acc-btn-secondary">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.8 }}>
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Export
            </button>
            <button className="acc-btn-primary" onClick={openAdd}>
              <span className="plus-icon" style={{ fontSize: '1.2rem', lineHeight: 1 }}>+</span> Add Budget Line
            </button>
          </div>
        </div>

        <div className="acc-table-wrapper products-table-container">
          <table className="acc-table">
            <thead>
              <tr>
                <th>Category</th>
                <th>Type</th>
                <th className="text-right">Budgeted</th>
                <th className="text-right">Actual</th>
                <th className="text-right">Variance</th>
                <th style={{ width: '160px' }}>Progress</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>Loading budget...</td></tr>
              ) : error ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: '#ef4444' }}>{error}</td></tr>
              ) : budget.length === 0 ? (
                <tr><td colSpan={7}><div className="acc-empty"><div className="acc-empty-icon">📊</div><h4>No budget data for {financialYear}</h4></div></td></tr>
              ) : budget.map(b => {
                const budgeted = parseFloat(b.budgeted_amount || b.budgeted) || 0;
                const actual   = parseFloat(b.actual_amount   || b.actual)   || 0;
                const type     = b.type || 'Expense';
                const label    = b.account?.name || b.category || b.account_name || '—';
                const pct      = budgeted > 0 ? Math.min((actual / budgeted) * 100, 100) : 0;
                const variance = type.toLowerCase() === 'income' ? actual - budgeted : budgeted - actual;
                const color    = getProgressColor(pct);
                return (
                  <tr key={b.id}>
                    <td style={{ fontWeight: 500 }}>{label}</td>
                    <td>
                      <span className={`acc-badge ${type.toLowerCase() === 'income' ? 'acc-badge-green' : 'acc-badge-red'}`}>{type}</span>
                    </td>
                    <td className="text-right">${budgeted.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                    <td className="text-right">${actual.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                    <td className="text-right" style={{ fontWeight: 600, color: variance >= 0 ? '#10b981' : '#ef4444' }}>
                      {variance >= 0 ? '+' : ''}${variance.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <div className="budget-progress-bar" style={{ flex: 1 }}>
                          <div className={`budget-progress-fill ${color}`} style={{ width: `${pct}%` }} />
                        </div>
                        <span style={{ fontSize: '0.75rem', color: 'var(--etpos-text-muted,#64748b)', minWidth: '36px' }}>{pct.toFixed(0)}%</span>
                      </div>
                    </td>
                    <td className="text-center">
                      <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'center' }}>
                        <button onClick={() => openEdit(b)} style={{ background: 'none', border: '1px solid var(--etpos-border,#e2e8f0)', borderRadius: '0.35rem', padding: '0.25rem 0.6rem', cursor: 'pointer', fontSize: '0.78rem' }}>Edit</button>
                        <button onClick={() => handleDelete(b.id)} style={{ background: 'none', border: '1px solid #fca5a5', borderRadius: '0.35rem', padding: '0.25rem 0.6rem', cursor: 'pointer', fontSize: '0.78rem', color: '#ef4444' }}>Del</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-container filter-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editRow ? 'Edit Budget Line' : 'Add Budget Line'}</h3>
              <button className="modal-close-btn" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div className="modal-content">
              <div className="acc-form-grid">
                <div className="acc-form-group" style={{ gridColumn: 'span 2' }}>
                  <label>Category</label>
                  <input value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} placeholder="e.g. Marketing" />
                </div>
                <div className="acc-form-group">
                  <label>Type</label>
                  <select value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))}>
                    <option>Income</option>
                    <option>Expense</option>
                  </select>
                </div>
                <div className="acc-form-group">
                  <label>Budgeted Amount</label>
                  <input type="number" value={form.budgeted} onChange={e => setForm(p => ({ ...p, budgeted: e.target.value }))} placeholder="0.00" />
                </div>
                <div className="acc-form-group">
                  <label>Actual Amount</label>
                  <input type="number" value={form.actual} onChange={e => setForm(p => ({ ...p, actual: e.target.value }))} placeholder="0.00" />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-btn close-btn" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="modal-btn primary-btn" onClick={handleSave}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Budget;
